import type { ReactElement } from 'react';

import { EducationInquiryCard } from '@/widgets/education-inquiry-card';
import { useVideos } from '@/entities/video';

import { VideoAssetGroup } from './video-asset-group';

/**
 * 자료실 — 1차 오픈 범위 §3.1 O-02 (/library).
 *
 * **영상 단위로 묶는다.** 유튜브에서 넘어온 사람은 「자료」를 찾아 오는 게 아니라
 * 「방금 본 영상의 자료」를 찾아 온다. 자료를 평평하게 늘어놓으면 자기 것을 골라내야 한다.
 *
 * 필터와 검색은 두지 않는다 — 영상이 몇 편인 시점의 검색은 「결과 없음」을 만드는 장치다.
 */
export function LibraryPage(): ReactElement {
  const videos = useVideos();
  const videoItems = videos.data?.items ?? [];

  return (
    <div className="u-container flex flex-col gap-10 py-10">
      <section aria-labelledby="library-hero-heading" className="flex flex-col gap-2">
        <span className="font-mono text-[10px] text-muted-foreground uppercase tracking-[0.12em]">
          From YouTube
        </span>
        <h1 id="library-hero-heading" className="text-[1.75rem] font-bold tracking-[-0.04em]">
          영상에서 소개한 자료
        </h1>
        <p className="text-[14px] text-muted-foreground">
          영상별로 정리해 뒀습니다. 방금 보신 영상을 찾아 바로 받으세요. 전부 무료입니다.
        </p>
      </section>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_17rem] lg:items-start lg:gap-10">
        <div className="min-w-0">
          {videos.isPending ? (
            <div aria-busy="true" className="h-64 animate-pulse rounded-lg bg-secondary" />
          ) : videos.isError ? (
            // LIB-U-01 AC-4 — 실패를 빈 목록처럼 보여주면 자료실이 망한 걸로 읽힌다
            <div className="flex flex-col items-center gap-3 rounded-lg border border-border border-dashed p-12 text-center">
              <p className="text-[15px]">자료를 불러오지 못했습니다</p>
              <button
                type="button"
                onClick={() => void videos.refetch()}
                className="text-[13px] underline underline-offset-4"
              >
                다시 시도
              </button>
            </div>
          ) : videoItems.length === 0 ? (
            // LIB-U-01 AC-3 — 자료 0개는 고장이 아니라 상태다
            <p className="rounded-lg border border-border border-dashed p-12 text-center text-[15px]">
              준비 중입니다
            </p>
          ) : (
            <ul className="flex flex-col gap-5">
              {videoItems.map((video) => (
                <li key={video.id}>
                  <VideoAssetGroup video={video} />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* 아래가 아니라 옆에 둔다 — 자료실은 목록만 훑고 나가는 화면이라 끝까지 안 내려간다 */}
        <EducationInquiryCard className="lg:sticky lg:top-24" />
      </div>
    </div>
  );
}
